import { Box, CssBaseline, IconButton, Slider, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// import { useLaunchStore } from "@/shared/stores/useLaunchStore";
import { getFirebaseMessaging } from "@/modules/auth/utils";
import { useGetBookings } from "@/shared/api/booking.api";
import {
  getActiveCardAudio,
  pauseActiveCardAudio,
  subscribeCardAudioState,
} from "@/shared/components/audio/singleCardAudio";
import { ROUTES, SocketNotificationType } from "@/shared/constants";
import { useAuth } from "@/shared/stores/useAuth";
import { useNotificationStore } from "@/shared/stores/useNotificationStore";
import { useCurrentTalentProfile } from "@/shared/stores/useProfile";
import { useSidebarStore } from "@/shared/stores/useSidebarStore";
import { SocketNotificationData } from "@/shared/types";
import {
  getTalentSocketNotification,
  getUserSocketNotification,
} from "@/shared/utils/notificationHandler";
import { playNotificationSound } from "@/shared/utils/notificationSound";
import SocketHandler from "@/shared/utils/socketHandler";
import { isFlutterEmbeddedMobileViewport } from "@/shared/utils/taasEmbeddedContext";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import { onMessage } from "firebase/messaging";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import MobileFooterView from "../footer/MobileFooterView";
import { Header } from "../header";
import UserSidebar from "../sidebar/UserSidebar";
import EmbeddedMoreSheet from "./EmbeddedMoreSheet";
import LayoutMain from "./LayoutMain";
import { useLaunchStore } from "./useLaunchStore";

const UserLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const { t } = useTranslation();
  const { currentNotification, setCurrentNotification, clearCurrentNotification } =
    useNotificationStore();

  const { userInfo } = useAuth();
  const { data } = useGetBookings({
    key: ["bookings", userInfo?.id],
    options: {
      enabled: false,
    },
  });

  const { currentProfile } = useCurrentTalentProfile();

  // Synchronize before paint: mobile starts closed without a drawer flash, while
  // desktop still starts expanded. This also handles viewport breakpoint changes.
  useLayoutEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);
  const navigate = useNavigate();
  const [badgeVisible, setBadgeVisible] = useState(false);

  const [socketNotification, setSocketNotification] = useState<any>([]);
  const [coffeeJoinChannel, setCoffeeJoinChannel] = useState<{
    channel_name: string;
    mode: string;
  } | null>(null);

  const socketSubscribedRef = useRef(false);
  useEffect(() => {
    // Prevent multiple subscriptions
    if (socketSubscribedRef.current || !userInfo?.id) {
      return;
    }

    try {
      // Get socket instance, open connection with auth token, then subscribe
      const socketHandler = SocketHandler.getInstance();
      socketHandler.connect();

      socketHandler.subscribeToNotification(userInfo.id.toString(), (message) => {
        if (message) {
          const notification = currentProfile?.id
            ? getTalentSocketNotification(message, currentProfile?.id)
            : getUserSocketNotification(message);
          if (notification) {
            setSocketNotification((prev: any) => [...prev, notification]);
            setBadgeVisible(true);
            setCurrentNotification(notification);
            // Play notification sound
            playNotificationSound();
            console.log("notification are here in user layout", notification);
          }
        }

        // Handle the message here
      });

      socketSubscribedRef.current = true;
      console.log("Successfully subscribed to notification socket");
    } catch (error) {
      console.error("Failed to subscribe to socket:", error);
    }

    // Cleanup subscription when component unmounts
    return () => {
      try {
        if (userInfo?.id) {
          const socketHandler = SocketHandler.getInstance();
          socketHandler.unsubscribeFromNotification(userInfo.id.toString());
          socketSubscribedRef.current = false;
          console.log("Successfully unsubscribed from notification socket");
        }
      } catch (error) {
        console.error("Failed to unsubscribe from socket:", error);
      }
    };
  }, [userInfo?.id, currentProfile?.id]);

  // coffee call
  const setCoffeeCallNotification = useSidebarStore((state) => state.setCoffeeCallNotification);

  //regular call notification
  const setRegularCallNotification = useSidebarStore((state) => state.setRegularCallNotification);

  // group call notification
  const setGroupCallNotification = useSidebarStore((state) => state.setGroupCallNotification);

  //  call confirmation
  const setCallConfirmation = useSidebarStore((state) => state.setCallConfirmation);

  //  call remainder
  const setCallRemainder = useSidebarStore((state) => state.setCallRemainder);

  const regularCallNotificationFromLocal = JSON.parse(
    localStorage.getItem("regularCallNotification") || "null"
  );
  const groupCallNotificationFromLocal = JSON.parse(
    localStorage.getItem("groupCallNotification") || "null"
  );
  const coffeeCallNotificationfromLocal = JSON.parse(
    localStorage.getItem("coffeeCallNotification") || "null"
  );

  useEffect(() => {
    if (!data?.data) return;

    // Coffee call
    if (coffeeCallNotificationfromLocal?.id) {
      const coffeeMatch = data.data.find(
        (item: any) => item.id === coffeeCallNotificationfromLocal.id
      );
      if (coffeeMatch && coffeeMatch.status == "Completed") {
        localStorage.removeItem("coffeeCallNotification");
        setCoffeeCallNotification(null); // Clear Zustand
      }
    }

    // Regular call
    if (regularCallNotificationFromLocal?.data?.id) {
      const regularMatch = data.data.find(
        (item: any) => Number(item.id) === Number(regularCallNotificationFromLocal.data.id)
      );
      if (regularMatch && regularMatch.status === "Completed") {
        localStorage.removeItem("regularCallNotification");
        setRegularCallNotification(null); // Clear Zustand
      }
    }

    // group call
    if (groupCallNotificationFromLocal?.data?.id) {
      const groupMatch = data.data.find(
        (item: any) => Number(item.id) === Number(groupCallNotificationFromLocal.data.id)
      );
      if (groupMatch && groupMatch.status === "Completed") {
        localStorage.removeItem("groupCallNotification");
        setRegularCallNotification(null); // Clear Zustand
      }
    }
  }, [
    data,
    coffeeCallNotificationfromLocal,
    groupCallNotificationFromLocal,
    regularCallNotificationFromLocal,
  ]); // Re-run only when bookings data changes

  useEffect(() => {
    if (socketNotification.length > 0) {
      socketNotification.forEach((notification: SocketNotificationData) => {
        if (notification.notification_type === "coffee-call-started") {
          enqueueSnackbar("Incoming coffee call!", { variant: "info" });
          setCoffeeJoinChannel({
            channel_name: notification?.channel_name || "",
            mode: notification?.mode || "Video",
          });
          setCoffeeCallNotification(notification);
          localStorage.setItem("coffeeCallNotification", JSON.stringify(notification));
        } else if (notification.notification_type === "coffee-call-ended") {
          setCoffeeJoinChannel(null);
        } else if (notification.notification_type === SocketNotificationType.BookingStarted) {
          if (notification.is_group_session) {
            enqueueSnackbar("Incoming group call!", { variant: "info" });
            setGroupCallNotification(notification);
            localStorage.setItem("groupCallNotification", JSON.stringify(notification));
          } else {
            enqueueSnackbar("Incoming call!", { variant: "info" });
            setRegularCallNotification(notification);
            localStorage.setItem("regularCallNotification", JSON.stringify(notification));
          }
        } else {
          setCoffeeJoinChannel(null);
        }
      });
    }
  }, [socketNotification]);

  useEffect(() => {
    if (currentNotification?.notification_type === SocketNotificationType.FlashCardBookingRefund) {
      enqueueSnackbar(
        t("flash_card_refund_notification", {
          title: currentNotification?.title,
          price: currentNotification?.price,
        }),
        { variant: "success" }
      );
      clearCurrentNotification();
    }

    if (currentNotification?.notification_type === SocketNotificationType.FlashCardStarted) {
      enqueueSnackbar(t("the_flash_card_you_bought_has_started"), {
        variant: "success",
      });
      clearCurrentNotification();
    }
  }, [
    currentNotification?.title,
    currentNotification?.notification_type,
    clearCurrentNotification,
  ]);

  useEffect(() => {
    const instance = getFirebaseMessaging();
    if (!instance) return undefined;

    const unsubscribe = onMessage(instance, (payload) => {
      console.log("payload", payload);

      if (
        (payload.data?.type === "group_session" || payload.data?.type === "regular_session") &&
        payload?.notification?.title?.includes("Healthy Reminder")
      ) {
        enqueueSnackbar(payload?.notification?.body, { variant: "info" });
        setCallRemainder(payload);
        localStorage.setItem("callRemainder", JSON.stringify(payload));
      }

      if (payload.data?.type === "booking_accepted") {
        enqueueSnackbar("Your booking has been accepted!", { variant: "info" });
        setCallConfirmation(payload);
        localStorage.setItem("callConfirmation", JSON.stringify(payload));
      }
    });

    return () => unsubscribe();
  }, []);

  const handleClose = () => {
    setDrawerOpen(false);
  };

  const showEmbeddedBottomNav = isFlutterEmbeddedMobileViewport(isMobile) || isMobile;

  return (
    <Box sx={{ display: "flex", bgcolor: "var(--page-bg)", minHeight: "100dvh" }}>
      <CssBaseline />
      {/* Overlay lives here — above the <Outlet> — so it persists across route changes */}
      <GlobalLaunchOverlay />
      <GlobalAudioMiniPlayer />
      <Header
        setBadgeVisible={setBadgeVisible}
        badgeVisible={badgeVisible}
        socketNotification={socketNotification}
        toggleDrawer={toggleDrawer}
      />
      <UserSidebar
        handleClose={handleClose}
        open={drawerOpen}
        toggleDrawer={toggleDrawer}
        coffeeJoinChannel={coffeeJoinChannel?.channel_name || ""}
        coffeeJoinMode={coffeeJoinChannel?.mode || "Video"}
      />
      <LayoutMain showBottomNav={showEmbeddedBottomNav} />

      {showEmbeddedBottomNav && <MobileFooterView variant="user" />}

      <EmbeddedMoreSheet
        open={moreSheetOpen}
        onClose={() => setMoreSheetOpen(false)}
        actions={[
          {
            key: "notifications",
            label: "Notifications",
            onClick: () => navigate(ROUTES.NOTIFICATION),
          },
          {
            key: "settings",
            label: "Settings",
            onClick: () => navigate(ROUTES.SETTINGS),
          },
        ]}
      />
    </Box>
  );
};

// ── Global launch overlay — rendered ABOVE the Outlet so it survives navigation ─
const GlobalAudioMiniPlayer: React.FC = () => {
  const [hasTrack, setHasTrack] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [artwork, setArtwork] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    let trackedAudio: HTMLAudioElement | null = null;

    const syncFrom = (audio: HTMLAudioElement | null) => {
      if (!audio) {
        setHasTrack(false);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        return;
      }

      const hasSource = Boolean(audio.currentSrc || audio.src);
      const ended = audio.ended || false;
      const shouldShow = hasSource && (!ended || audio.currentTime > 0);
      setHasTrack(shouldShow);
      setIsPlaying(!audio.paused && !ended);
      setCurrentTime(audio.currentTime || 0);
      setDuration(Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0);
      setTitle(audio.dataset.singleCardTitle || "Now playing");
      setArtist(audio.dataset.singleCardArtist || "");
      setArtwork(audio.dataset.singleCardArtwork || "");
    };

    const bindTrackedAudio = (audio: HTMLAudioElement) => {
      const onPlay = () => {
        setIsDismissed(false);
        syncFrom(audio);
      };
      const onSync = () => syncFrom(audio);
      const onEnded = () => {
        syncFrom(audio);
        if (audio.ended) {
          setHasTrack(false);
        }
      };

      audio.addEventListener("play", onPlay);
      audio.addEventListener("pause", onSync);
      audio.addEventListener("timeupdate", onSync);
      audio.addEventListener("loadedmetadata", onSync);
      audio.addEventListener("ended", onEnded);

      return () => {
        audio.removeEventListener("play", onPlay);
        audio.removeEventListener("pause", onSync);
        audio.removeEventListener("timeupdate", onSync);
        audio.removeEventListener("loadedmetadata", onSync);
        audio.removeEventListener("ended", onEnded);
      };
    };

    let unbindTracked: (() => void) | null = null;

    const unsubscribe = subscribeCardAudioState((audio: any) => {
      unbindTracked?.();
      unbindTracked = null;
      trackedAudio = audio;

      if (audio) {
        unbindTracked = bindTrackedAudio(audio);
        syncFrom(audio);
      } else {
        syncFrom(null);
      }
    });

    return () => {
      unsubscribe();
      unbindTracked?.();
    };
  }, []);

  if (!hasTrack || isDismissed) return null;

  const audio = getActiveCardAudio();
  if (!audio) return null;

  const togglePlayPause = () => {
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  };

  const seekTo = (_: Event, value: number | number[]) => {
    const next = Array.isArray(value) ? value[0] : value;
    if (!Number.isFinite(next)) return;
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const jump = (delta: number) => {
    if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const next = Math.min(Math.max(0, audio.currentTime + delta), audio.duration);
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const stopPlayback = () => {
    pauseActiveCardAudio();
    setCurrentTime(0);
    setIsPlaying(false);
    setIsDismissed(true);
    setIsExpanded(false);
    setArtwork("");
  };

  const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Box
      sx={{
        position: "fixed",
        right: { xs: 12, sm: 18 },
        bottom: { xs: 14, sm: 18 },
        zIndex: 1700,
        width: isExpanded ? { xs: "calc(100vw - 24px)", sm: 380 } : { xs: "72vw", sm: 360 },
        maxWidth: 420,
        borderRadius: isExpanded ? "18px" : "999px",
        px: isExpanded ? 1.4 : 1.25,
        py: isExpanded ? 1.2 : 0.9,
        color: "#fff",
        background:
          "linear-gradient(165deg, rgba(47,48,54,0.94) 0%, rgba(31,32,36,0.92) 48%, rgba(21,22,27,0.95) 100%)",
        border: "1px solid rgba(255,255,255,0.14)",
        backdropFilter: "blur(16px) saturate(160%)",
        boxShadow: "0 14px 36px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.08)",
        transition: "all 0.22s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            flexShrink: 0,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.25)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
            background: artwork
              ? `url(${artwork}) center / cover no-repeat`
              : "radial-gradient(circle at 30% 20%, rgba(165,180,252,0.9), rgba(67,56,202,0.9))",
          }}
        />
        <Box
          sx={{ minWidth: 0, flex: 1, cursor: "pointer" }}
          onClick={() => setIsExpanded((v) => !v)}
        >
          <Box
            component="div"
            sx={{
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title || "Now playing"}
          </Box>
          {!!artist && (
            <Box
              component="div"
              sx={{
                mt: 0.2,
                fontSize: 11,
                opacity: 0.8,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {artist}
            </Box>
          )}
        </Box>

        <IconButton
          onClick={togglePlayPause}
          sx={{
            width: 34,
            height: 34,
            background: "rgba(255,255,255,0.16)",
            color: "#fff",
            "&:hover": { background: "rgba(255,255,255,0.22)" },
          }}
        >
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: 18 }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>

        <IconButton
          onClick={() => setIsExpanded((v) => !v)}
          sx={{
            width: 30,
            height: 30,
            color: "rgba(255,255,255,0.86)",
            transform: isExpanded ? "rotate(180deg)" : "none",
            transition: "transform 0.2s ease",
          }}
        >
          <ExpandMoreIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <IconButton
          onClick={stopPlayback}
          sx={{
            width: 30,
            height: 30,
            color: "rgba(255,255,255,0.78)",
            background: "rgba(255,255,255,0.06)",
            "&:hover": { background: "rgba(255,255,255,0.14)" },
          }}
          title="Close player"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {isExpanded && (
        <Box sx={{ mt: 1 }}>
          <Slider
            size="small"
            min={0}
            max={Math.max(duration, 1)}
            value={Math.min(currentTime, Math.max(duration, 1))}
            onChange={seekTo}
            sx={{
              color: "#9ec2ff",
              py: 0.25,
              "& .MuiSlider-thumb": { width: 10, height: 10 },
            }}
          />
          <Box
            sx={{
              mt: 0.1,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              color: "rgba(255,255,255,0.75)",
            }}
          >
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </Box>
          <Box
            sx={{ mt: 0.6, display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <IconButton onClick={() => jump(-10)} sx={{ color: "#fff" }}>
              <SkipPreviousIcon />
            </IconButton>
            <IconButton
              onClick={togglePlayPause}
              sx={{
                width: 42,
                height: 42,
                background: "rgba(255,255,255,0.18)",
                color: "#fff",
                "&:hover": { background: "rgba(255,255,255,0.26)" },
              }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton onClick={() => jump(10)} sx={{ color: "#fff" }}>
              <SkipNextIcon />
            </IconButton>
            <IconButton
              onClick={stopPlayback}
              sx={{ color: "rgba(255,255,255,0.78)" }}
              title="Stop"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── Global launch overlay — rendered ABOVE the Outlet so it survives navigation ─
const GlobalLaunchOverlay: React.FC = () => {
  const { payload, clear } = useLaunchStore();
  const [expanded, setExpanded] = useState(false);
  const prevPayloadRef = useRef<typeof payload>(null);

  useEffect(() => {
    if (!payload) {
      setExpanded(false);
      return;
    }
    // Only kick off the expansion when a new payload arrives
    if (payload !== prevPayloadRef.current) {
      prevPayloadRef.current = payload;
      setExpanded(false);
      let id1: number, id2: number;
      id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setExpanded(true));
      });
      // Auto-clear after animation + navigation settle
      const t = setTimeout(() => clear(), 500);
      return () => {
        cancelAnimationFrame(id1);
        cancelAnimationFrame(id2);
        clearTimeout(t);
      };
    }
  }, [payload, clear]);

  if (!payload) return null;

  const { bg, glowColor, iconColor, icon, originX, originY, startRadius } = payload;
  const endR = Math.hypot(window.innerWidth, window.innerHeight) + 50;
  const ease = "cubic-bezier(0.32, 0.72, 0, 1)";
  const dur = "0.46s";

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: bg,
        pointerEvents: "none",
        clipPath: expanded
          ? `circle(${endR}px at ${originX}px ${originY}px)`
          : `circle(${startRadius}px at ${originX}px ${originY}px)`,
        transition: expanded ? `clip-path ${dur} ${ease}` : "none",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: "linear-gradient(140deg, rgba(255,255,255,0.20) 0%, transparent 40%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: originY,
          left: originX,
          transform: expanded ? "translate(-50%,-50%) scale(3.5)" : "translate(-50%,-50%) scale(1)",
          opacity: expanded ? 0 : 1,
          transition: expanded ? `transform ${dur} ${ease}, opacity 0.25s ease` : "none",
          color: iconColor,
          filter: `drop-shadow(0 0 20px ${glowColor})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
    </Box>
  );
};

export default UserLayout;
